import * as React from "react";
import { cn } from "../../lib/utils";

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

// `useLayoutEffect` warns when run on the server; fall back to `useEffect`
// there so the component is safe to render under SSR (e.g. Next.js apps).
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

const TextArea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, value, onChange, onInput, ...props }, ref) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    // Track the node ourselves (to measure it and find its owning form) while
    // still forwarding the ref to callers.
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [ref]
    );

    // Size the field to fit its content. Collapsing to "auto" first lets it
    // *shrink* as well as grow — otherwise `scrollHeight` is clamped to the
    // current box height. An empty field returns to "auto" (its natural
    // min-height) rather than a 0px line.
    const adjustHeight = React.useCallback(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = el.value ? `${el.scrollHeight}px` : "auto";
    }, []);

    // Re-measure after any render that may change the content: the initial
    // mount (prefilled `defaultValue`/`value`) and controlled `value` updates.
    useIsomorphicLayoutEffect(() => {
      adjustHeight();
    }, [value, adjustHeight]);

    // `form.reset()` clears/repopulates fields without firing an `input` event
    // — e.g. the chat composer clears the box after a message is sent — so the
    // auto-grown height would otherwise stay stuck. Collapse immediately
    // (correct for the common reset-to-empty case) and re-measure on the next
    // frame to cover a reset back to a non-empty default value.
    React.useEffect(() => {
      const form = innerRef.current?.form;
      if (!form) return;
      const handleReset = () => {
        const el = innerRef.current;
        if (el) el.style.height = "auto";
        requestAnimationFrame(adjustHeight);
      };
      form.addEventListener("reset", handleReset);
      return () => form.removeEventListener("reset", handleReset);
    }, [adjustHeight]);

    // Resize as the user types (fires for uncontrolled and controlled usage).
    const handleInput: React.FormEventHandler<HTMLTextAreaElement> = (event) => {
      adjustHeight();
      onInput?.(event);
    };

    return (
      <textarea
        {...props}
        ref={setRefs}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-vertical overflow-auto ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        value={value}
        onChange={onChange}
        onInput={handleInput}
      />
    );
  }
);

TextArea.displayName = "TextArea";

export { TextArea };
